import { Col, message, notification, Popconfirm, Row, Select, Space, Spin, Table, Tag } from 'antd'
import {
  Button,
  ButtonActionBar,
  DeleteAndUpdate,
  DropdownMenu,
  EmptyScreen,
  InputCell,
} from 'modules/core/components'
import { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import emptyStateImage from 'assets/images/compositions/empty-big.svg'
import configuration from 'modules/configuration'
import { AzureTenantCreateModal } from './components'
import { useTranslation } from 'react-i18next'
import { DeleteOutlined, EditOutlined } from '@ant-design/icons'
import { generateMessageError } from 'modules/core/utils'
import { usePagination } from 'modules/core/customHooks'

const AzureTenantsList = ({
  fetchAzureTenants,
  azureTenantsList,
  deleteAzureTenant,
  editAzureTenant,
}) => {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [deleteId, setDeleteId] = useState(null)
  const [editRow, setEditRow] = useState(null)
  const [dataCellEdit, setDataCellEdit] = useState({})
  const [isSaving, setIsSaving] = useState(false)
  const [tablePagination, setTablePagination] = usePagination()
  const [isLoading, setIsLoading] = useState(false)
  const { t } = useTranslation()

  useEffect(() => {
    setIsLoading(true)
    fetchAzureTenants({ ...tablePagination }).then(() => setIsLoading(false))
  }, [fetchAzureTenants, tablePagination])

  const dataSource = useMemo(
    () => (Array.isArray(azureTenantsList) ? azureTenantsList : azureTenantsList?.results || []),
    [azureTenantsList],
  )

  const activeOptions = useMemo(
    () => [
      { value: true, label: t('CONFIG_AZURE_TENANT_FIELD_ACTIVE_TRUE') },
      { value: false, label: t('CONFIG_AZURE_TENANT_FIELD_ACTIVE_FALSE') },
    ],
    [t],
  )

  const onCellEdit = (name, value) => {
    setDataCellEdit({ ...dataCellEdit, [`${name}`]: value })
  }

  const onConfirm = () => {
    setEditRow(null)
    setDataCellEdit({})
  }

  const onCancel = () => setEditRow(null)

  const handleDelete = () => {
    setIsSaving(true)
    deleteAzureTenant(deleteId)
      .then(() =>
        fetchAzureTenants({ ...tablePagination }).then(() => {
          message.success(t('FEEDBACK_CHANGES_SAVED_SUCCESS'), 8)
          setIsSaving(false)
          setDeleteId(null)
        }),
      )
      .catch((error) => {
        setIsSaving(false)
        setDeleteId(null)
        notification.error({
          message: t('FEEDBACK_DEFAULT_ERROR'),
          description: generateMessageError(error),
          duration: 0,
        })
      })
  }

  const handleEdit = (id) => {
    setIsSaving(true)
    const original = dataSource.find((item) => item.id === id)
    const { id: _id, client_name: _clientName, created_on: _createdOn, ...rest } = original
    const payload = { ...rest, ...dataCellEdit }
    editAzureTenant(id, payload)
      .then(() =>
        fetchAzureTenants({ ...tablePagination }).then(() => {
          message.success(t('FEEDBACK_CHANGES_SAVED_SUCCESS'), 8)
          setIsSaving(false)
          setDataCellEdit({})
          setEditRow(null)
        }),
      )
      .catch((error) => {
        setIsSaving(false)
        setDataCellEdit({})
        setEditRow(null)
        notification.error({
          message: t('FEEDBACK_DEFAULT_ERROR'),
          description: generateMessageError(error),
          duration: 0,
        })
      })
  }

  const columns = [
    {
      dataIndex: 'name',
      title: t('CONFIG_AZURE_TENANT_FIELD_NAME'),
      render: (value, row) => {
        if (editRow && row.id === editRow) {
          return <InputCell initialValue={value} name="name" onCellUpdate={onCellEdit} />
        }
        return value
      },
    },
    {
      dataIndex: 'azure_tenant_id',
      title: t('CONFIG_AZURE_TENANT_FIELD_TENANT_ID'),
      render: (value, row) => {
        if (editRow && row.id === editRow) {
          return (
            <InputCell initialValue={value} name="azure_tenant_id" onCellUpdate={onCellEdit} />
          )
        }
        return value
      },
    },
    {
      dataIndex: 'client_name',
      title: t('CONFIG_AZURE_TENANT_FIELD_CLIENT'),
    },
    {
      dataIndex: 'is_active',
      title: t('CONFIG_AZURE_TENANT_FIELD_ACTIVE'),
      render: (value, row) => {
        if (editRow && row.id === editRow) {
          return (
            <Select
              defaultValue={value}
              options={activeOptions}
              onChange={(val) => onCellEdit('is_active', val)}
              style={{ width: '100%' }}
            />
          )
        }
        return (
          <Tag color={value ? 'green' : 'red'}>
            {value
              ? t('CONFIG_AZURE_TENANT_FIELD_ACTIVE_TRUE')
              : t('CONFIG_AZURE_TENANT_FIELD_ACTIVE_FALSE')}
          </Tag>
        )
      },
    },
    {
      dataIndex: 'id',
      title: t('FIELD_ACTIONS'),
      align: 'right',
      render: (id) => {
        return id !== editRow ? (
          <Popconfirm
            placement="bottomRight"
            title={t('CONFIG_AZURE_TENANT_DELETE_CONFIRMATION')}
            okText={t('ACTION_DELETE')}
            onConfirm={() => handleDelete()}
            okButtonProps={{ loading: isSaving }}
            cancelButtonProps={{ disabled: isSaving }}
            cancelText={t('ACTION_CANCEL')}
            onCancel={() => setDeleteId(null)}
            visible={deleteId === id}
          >
            <Space size="middle">
              <DropdownMenu
                title={t('ACTION_MORE')}
                menu={[
                  {
                    title: t('ACTION_EDIT'),
                    icon: <EditOutlined />,
                    onClick: () => setEditRow(id),
                  },
                  {
                    title: t('ACTION_DELETE'),
                    icon: <DeleteOutlined />,
                    onClick: () => setDeleteId(id),
                  },
                ]}
              />
            </Space>
          </Popconfirm>
        ) : (
          <DeleteAndUpdate
            onCancel={onCancel}
            onConfirm={onConfirm}
            onSave={() => handleEdit(id)}
            dataCellEdit={dataCellEdit}
            loading={isSaving}
          />
        )
      },
    },
  ]

  return (
    <Spin spinning={isLoading}>
      {dataSource.length === 0 && !isLoading && (
        <EmptyScreen
          image={emptyStateImage}
          description={t('CONFIG_AZURE_TENANT_EMPTY_DESCRIPTION')}
          footer={
            <Button.Primary title="ACTION_CREATE_NEW" onClick={() => setShowCreateModal(true)} />
          }
        />
      )}
      {dataSource.length > 0 && (
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <ButtonActionBar>
              <Button.Primary title="ACTION_CREATE_NEW" onClick={() => setShowCreateModal(true)} />
            </ButtonActionBar>
          </Col>
          <Col span={24}>
            <Table
              size="small"
              columns={columns}
              dataSource={dataSource}
              rowKey="id"
              loading={isSaving}
              onChange={setTablePagination}
              pagination={{
                pageSize: tablePagination.page_size,
                current: tablePagination.page,
                total: azureTenantsList?.count ?? dataSource.length,
                size: 'small',
                pageSizeOptions: ['10', '20', '30'],
                showSizeChanger: true,
              }}
            />
          </Col>
        </Row>
      )}
      <AzureTenantCreateModal
        visible={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onRefetch={() => fetchAzureTenants({ ...tablePagination })}
      />
    </Spin>
  )
}

const mapStateToProps = (state) => ({
  azureTenantsList: configuration.selectors.getAzureTenantsList(state),
})

const mapDispatchToProps = {
  fetchAzureTenants: configuration.actions.fetchAzureTenants,
  deleteAzureTenant: configuration.actions.deleteAzureTenant,
  editAzureTenant: configuration.actions.editAzureTenant,
}

export default connect(mapStateToProps, mapDispatchToProps)(AzureTenantsList)
